import { useState } from 'react';
import { trackEvent } from '../lib/analytics';
import {
  CONTACT_FORM_COPY,
  CONTACT_PRODUCT_OPTIONS,
  hasValidPhone,
  leadFromContactForm,
} from '../lib/contactForm';
import { submitPublicLead } from '../lib/publicLead';
import type { SupportedLocale } from '../lib/localization';

type PublicContactFormProps = {
  locale: SupportedLocale;
  source: 'website' | 'landing';
  landingUrl?: string;
  submitLabel: string;
  variant?: 'themed' | 'dark';
  palette?: { border: string; heading: string; cardBg: string };
  darkMode?: boolean;
  accentColor?: string;
  extraTracking?: Record<string, unknown>;
  compact?: boolean;
};

export function PublicContactForm({
  locale,
  source,
  landingUrl,
  submitLabel,
  variant = 'themed',
  palette,
  darkMode = false,
  accentColor = '#1B7A74',
  extraTracking,
  compact = false,
}: PublicContactFormProps) {
  const copy = CONTACT_FORM_COPY[locale];
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const isDark = variant === 'dark';
  const inputClass = isDark
    ? 'w-full rounded-xl border border-white/15 bg-[#0B0F0F] px-3.5 py-2.5 text-sm text-paper placeholder:text-paper/45 focus:border-[#28F1E7] focus:outline-none focus:ring-1 focus:ring-[#28F1E7]'
    : 'w-full border border-ink/15 bg-transparent px-3 py-2 text-sm focus:border-[#1B7A74] focus:outline-none';
  const inputStyle = isDark
    ? undefined
    : compact
      ? undefined
      : {
          borderColor: palette?.border,
          backgroundColor: darkMode ? '#0f172a' : '#ffffff',
          color: palette?.heading,
        };
  const labelStyle = isDark ? undefined : { color: palette?.heading };
  const labelClass = isDark
    ? 'mb-2 block text-sm font-medium text-paper/70'
    : 'mb-2 block text-sm font-medium';

  return (
    <form
      className="space-y-4"
      style={
        isDark
          ? undefined
          : { borderColor: palette?.border, backgroundColor: palette?.cardBg }
      }
      onSubmit={async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const formData = new FormData(form);
        const payload = leadFromContactForm(formData, { source, landingUrl, locale });
        setError(null);
        if (payload.phone && !hasValidPhone(payload.phone)) {
          setError(copy.phoneInvalid);
          return;
        }
        setSending(true);
        try {
          await submitPublicLead(payload);
          trackEvent(source === 'landing' ? 'landing_quote_submit' : 'website_quote_submit', {
            locale,
            product: payload.productInterest,
            hasBudget: Boolean(payload.budget),
            ...extraTracking,
          });
          setSubmitted(true);
          form.reset();
        } catch (err: any) {
          setError(err?.message || copy.error);
        } finally {
          setSending(false);
        }
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelClass} style={labelStyle} htmlFor="contact-name">
            {copy.fullName}
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            className={inputClass}
            style={inputStyle}
            placeholder={copy.fullNamePlaceholder}
          />
        </div>
        {!compact && (
        <div className="sm:col-span-2">
          <label className={labelClass} style={labelStyle} htmlFor="contact-company">
            {copy.company}
          </label>
          <input
            id="contact-company"
            name="company"
            type="text"
            autoComplete="organization"
            className={inputClass}
            style={inputStyle}
            placeholder={copy.companyPlaceholder}
          />
        </div>
        )}
        {!compact && (
        <div>
          <label className={labelClass} style={labelStyle} htmlFor="contact-phone">
            {copy.phone}
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            className={inputClass}
            style={inputStyle}
            placeholder={copy.phonePlaceholder}
          />
        </div>
        )}
        <div className={compact ? "sm:col-span-2" : undefined}>
          <label className={labelClass} style={labelStyle} htmlFor="contact-email">
            {copy.email}
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className={inputClass}
            style={inputStyle}
            placeholder={copy.emailPlaceholder}
          />
        </div>
        {!compact && (
        <div className="sm:col-span-2">
          <label className={labelClass} style={labelStyle} htmlFor="contact-product">
            {copy.product}
          </label>
          <select
            id="contact-product"
            name="product"
            defaultValue=""
            className={inputClass}
            style={inputStyle}
          >
            <option value="" disabled>
              {copy.productPlaceholder}
            </option>
            {CONTACT_PRODUCT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option[locale]}
              </option>
            ))}
          </select>
        </div>
        )}
        <div className="sm:col-span-2">
          <label className={labelClass} style={labelStyle} htmlFor="contact-need">
            {copy.need}
          </label>
          <textarea
            id="contact-need"
            name="need"
            rows={compact ? 3 : 5}
            required
            className={inputClass}
            style={inputStyle}
            placeholder={copy.needPlaceholder}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass} style={labelStyle} htmlFor="contact-budget">
            {copy.budget}
          </label>
          <input
            id="contact-budget"
            name="budget"
            type="text"
            className={inputClass}
            style={inputStyle}
            placeholder={copy.budgetPlaceholder}
          />
        </div>
      </div>
      {!compact && (
      <p className={`text-xs ${isDark ? 'text-paper/45' : 'opacity-80'}`} style={isDark ? undefined : { color: palette?.heading }}>
        {copy.privacy}
      </p>
      )}
      <button
        type="submit"
        disabled={sending}
        className={
          isDark
            ? 'site-btn site-btn-sm w-full disabled:opacity-50'
            : compact
              ? 'w-full border border-ink px-6 py-3 text-sm text-ink transition hover:bg-ink hover:text-paper disabled:opacity-50'
              : 'w-full px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50'
        }
        style={isDark || compact ? undefined : { backgroundColor: accentColor }}
      >
        {sending ? copy.sending : submitLabel}
      </button>
      {error && (
        <p className={`text-center text-xs ${isDark ? 'text-red-300' : 'text-red-500'}`}>{error}</p>
      )}
      {submitted && (
        <p className={`text-center text-xs ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
          {copy.success}
        </p>
      )}
    </form>
  );
}
